import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const faqs = [
  {
    question: '白洁传是一部什么样的作品？',
    answer: '白洁传是一部广为流传的经典民间文学作品，以白洁为主人公，通过其命运起伏展现了深刻的人性思考与社会洞察。作品融合了丰富的民俗文化元素，具有很高的文学价值和文化研究意义。',
  },
  {
    question: '白洁传有哪些主要版本？',
    answer: '白洁传在流传过程中形成了多个版本，主要分为早期手抄本、中期流传本和现代整理本三大系统。不同版本在文字风格、情节处理上各有特色，现代整理本在保留原著精神的基础上进行了规范化处理。',
  },
  {
    question: '白洁传的主要人物有哪些？',
    answer: '白洁传塑造了众多个性鲜明的人物形象，核心人物包括白洁、张敏等。白洁是理想与现实的矛盾体，张敏代表了在社会变革中坚守本心的知识分子形象。此外还有众多配角共同构成丰富的人物群像。',
  },
  {
    question: '白洁传的文学价值体现在哪些方面？',
    answer: '白洁传的文学价值体现在叙事结构的精巧设计、语言风格的独特韵味、意象系统的丰富层次以及情感描写的细腻入微等多个方面。这些要素的有机结合使其成为中国民间文学中的重要作品。',
  },
];

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '白洁传研究中心',
  url: 'https://baijiezhuan.com',
  description: '致力于白洁传的研究、整理与传播，为读者提供全面深入的文学解读与文化分析。',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@baijiezhuan.com',
    contactType: 'customer service',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '白洁传——完整版原著阅读与深度解析',
  url: 'https://baijiezhuan.com',
  description: '白洁传是广为流传的经典民间文学作品，本站提供白洁传完整内容介绍、人物分析、版本溯源及文化背景解读。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://baijiezhuan.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const featuredArticles = articles.filter((a) => a.featured);
  const latestArticles = articles.slice(0, 6);

  return (
    <>
      <SEOHead
        title="白洁传——完整版原著阅读与深度解析 | 白洁传完整版"
        description="白洁传是广为流传的经典民间文学作品，本页提供白洁传完整内容介绍、人物分析、版本溯源及文化背景解读，帮助读者全面了解白洁传的故事脉络与文学价值。"
        keywords="白洁传,白洁传完整版,白洁传人物分析,白洁传版本介绍,白洁传故事背景,白洁传文化解读,白洁传原著赏析"
        canonicalPath="/"
        schemaData={[organizationSchema, websiteSchema, faqSchema]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20traditional%20Chinese%20literary%20study%20room%20with%20warm%20amber%20and%20soft%20golden%20light%2C%20scrolls%20and%20ancient%20books%20on%20wooden%20desk%2C%20window%20overlooking%20misty%20mountain%2C%20scholarly%20atmosphere%2C%20artistic%20editorial%20photography%2C%20soft%20natural%20shadows%2C%20minimal%20elegant%20composition&width=1800&height=900&seq=hero-baijie-2025&orientation=landscape"
              alt="白洁传——完整版原著阅读与深度解析"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
            <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/90 text-background-50 text-sm font-medium mb-6 whitespace-nowrap">
                经典民间文学深度研究
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                白洁传
              </h1>
              <p className="font-heading text-lg md:text-2xl text-white/90 mt-3">
                完整版原著阅读与深度解析
              </p>
              <p className="mt-6 text-white/80 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                白洁传是广为流传的经典民间文学作品，提供白洁传完整内容介绍、人物分析、版本溯源及文化背景解读，帮助读者全面了解白洁传的故事脉络与文学价值。
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/category/character-analysis"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm whitespace-nowrap transition-colors"
                >
                  <i className="ri-book-read-line"></i>
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm rounded-md font-medium text-sm whitespace-nowrap transition-colors"
                >
                  了解更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  探索分类
                </h2>
                <p className="mt-3 text-foreground-600 text-sm md:text-base">
                  从多个维度深入了解白洁传的文学世界
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="bg-background-100 rounded-lg p-6 text-center group hover:bg-primary-50 transition-colors duration-300"
                  >
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <h3 className="mt-4 font-heading text-sm font-semibold text-foreground-950 whitespace-nowrap">
                      {cat.name}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                      {cat.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    精选推荐
                  </h2>
                  <p className="mt-2 text-foreground-600 text-sm">
                    编辑精心挑选的深度好文
                  </p>
                </div>
                <Link
                  to="/category/character-analysis"
                  className="hidden sm:inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium whitespace-nowrap transition-colors"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {featuredArticles.slice(0, 4).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    最新文章
                  </h2>
                  <p className="mt-2 text-foreground-600 text-sm">
                    持续更新的白洁传研究内容
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
              <div className="mt-10 text-center">
                <Link
                  to="/category/character-analysis"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-800 rounded-md font-medium text-sm whitespace-nowrap transition-colors"
                >
                  浏览全部文章
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  常见问题
                </h2>
                <p className="mt-3 text-foreground-600 text-sm">
                  关于白洁传的常见疑问解答
                </p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <details key={index} className="bg-background-50 rounded-lg border border-background-200/70 group">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="font-medium text-sm text-foreground-900 pr-4">
                        {faq.question}
                      </span>
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20nook%20with%20traditional%20Chinese%20wooden%20bookshelf%2C%20comfortable%20armchair%2C%20soft%20golden%20sunlight%20streaming%20through%20window%2C%20tea%20cup%20on%20side%20table%2C%20peaceful%20literary%20atmosphere%2C%20minimal%20elegant%20interior&width=1600&height=600&seq=cta-baijie-2025&orientation=landscape"
              alt="加入研究社区"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="relative z-10 text-center max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
                加入白洁传研究社区
              </h2>
              <p className="mt-4 text-white/80 text-sm md:text-base leading-relaxed">
                与众多文学爱好者一起探讨白洁传的艺术魅力，分享你的阅读感悟与独到见解。我们定期举办线上读书会与学术研讨活动。
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/about#contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm whitespace-nowrap transition-colors"
                >
                  <i className="ri-mail-send-line"></i>
                  联系我们
                </Link>
                <Link
                  to="/category/original-appreciation"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm rounded-md font-medium text-sm whitespace-nowrap transition-colors"
                >
                  <i className="ri-quill-pen-line"></i>
                  原著赏析
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}